import { useCallback, useMemo, useState } from 'react';

import { AgGridTable } from 'core/components';
import { SortDirection, TableLayoutType } from 'core/enums';
import { AgGridClientTableProvider } from 'core/states';
import { PricingProfileDetailsDrawer } from 'pricing/components';
import { useGetCorporatePricingProfiles } from 'pricing/hooks';
import { ICorporatePricingProfile } from 'pricing/types';

import { LinkProjectPricingProfileButton } from './actions';
import { getColumnDef } from './columns';

interface IProps {
  projectId: number;
}

export const PricingProfileList = ({ projectId }: IProps) => {
  const [selectedProfile, setSelectedProfile] = useState<ICorporatePricingProfile | null>(null);

  const { records, isLoading, loadingPercentage, currentPage } =
    useGetCorporatePricingProfiles<ICorporatePricingProfile>({
      viewBy: { project: projectId },
    });

  const onNameClick = useCallback((data: ICorporatePricingProfile) => {
    setSelectedProfile(data);
  }, []);

  const onCloseDrawer = useCallback(() => {
    setSelectedProfile(null);
  }, []);

  const columnDefs = useMemo(
    () => getColumnDef({ onNameClick, projectId }),
    [onNameClick, projectId]
  );

  const initialState = {
    sort: {
      sortModel: [{ colId: 'updatedAt', sort: SortDirection.DESC }],
    },
  };

  const actionButtons = useMemo(() => {
    return [
      <LinkProjectPricingProfileButton
        key="linkProjectPricingProfileButton"
        projectId={projectId}
      />,
    ];
  }, [projectId]);

  return (
    <>
      <AgGridClientTableProvider
        tableId="projectPricingProfilesTable"
        entityType="PricingProfile"
        columnDefs={columnDefs}
        data={records}
        isDataLoading={isLoading}
        loadingPercentage={loadingPercentage}
        dataCurrentPage={currentPage}
        initialState={initialState}
        hasLayoutActions
        hasManageFilters
        hasRowSelection={false}
        layoutType={TableLayoutType.PROJECT_PRICING_PROFILE}
      >
        <AgGridTable actionButtons={actionButtons} />
      </AgGridClientTableProvider>
      <PricingProfileDetailsDrawer
        isOpen={!!selectedProfile}
        onClose={onCloseDrawer}
        pricingProfile={selectedProfile}
        projectId={projectId}
        projectPricingProfileId={selectedProfile?.projectPricingProfileId}
      />
    </>
  );
};
